import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  fetchLines, fetchJourneys,
  Journey, FilterMode,
  formatTime, statusColour,
} from './analysisApi';

// ---- Colours ----
const C = {
  bg:           '#0f2033',
  bgHover:      '#162840',
  bgSelected:   '#1a3550',
  border:       'rgba(240,165,0,0.15)',
  label:        'rgba(255,255,255,0.35)',
  text:         'rgba(255,255,255,0.75)',
  textMuted:    'rgba(255,255,255,0.35)',
  textDim:      'rgba(255,255,255,0.2)',
  filterActive: '#f0a500',
  filterBg:     'rgba(240,165,0,0.12)',
};

function berlinToday(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function formatDayHeading(isoDate: string): string {
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ---- Occupancy bar ----
function occupancyColour(pct: number | null): string {
  if (pct === null || pct === undefined) return 'rgba(255,255,255,0.1)';
  if (pct >= 70) return '#FD0000';
  if (pct >= 30) return '#FFB600';
  return '#6FAC5B';
}

const OccupancyBar: React.FC<{ occupancy: number | null }> = ({ occupancy }) => {
  const colour = occupancyColour(occupancy);
  const width  = occupancy !== null ? `${Math.min(100, occupancy)}%` : '0%';
  return (
    <Box sx={{ mt: '4px', height: '3px', width: '100%', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width, backgroundColor: colour, borderRadius: '2px', transition: 'width 0.4s ease, background-color 0.4s ease' }} />
    </Box>
  );
};

type ActiveFilter = 'live' | 'recent' | 'all' | 'date';

type Props = {
  selectedJourneyId: string | null;
  onSelectJourney:   (journey: Journey) => void;
};

const MessagesSidebar: React.FC<Props> = ({ selectedJourneyId, onSelectJourney }) => {
  const today = berlinToday();

  const [lines,           setLines]           = useState<string[]>([]);
  const [selectedLine,    setSelectedLine]    = useState<string | null>(null);
  const [activeFilter,    setActiveFilter]    = useState<ActiveFilter>('live');
  const [pickedDate,      setPickedDate]      = useState<string>(today);
  const [journeys,        setJourneys]        = useState<Journey[]>([]);
  const [loadingLines,    setLoadingLines]    = useState(true);
  const [loadingJourneys, setLoadingJourneys] = useState(false);
  const [error,           setError]           = useState<string | null>(null);

  // Occupancy map: route_id → live.occupancy (from Firestore lines/ collection)
  const [occupancyMap, setOccupancyMap] = useState<Record<string, number | null>>({});

  const effectiveDay = activeFilter === 'date' ? pickedDate : today;
  const apiFilter: FilterMode = activeFilter === 'date' ? 'today' :
                                activeFilter === 'all'  ? 'today' :
                                activeFilter;

  // ---- Load lines ----
  useEffect(() => {
    setLoadingLines(true);
    fetchLines(effectiveDay)
      .then(r => {
        setLines(r.lines);
        if (r.lines.length > 0) setSelectedLine(r.lines[0]);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoadingLines(false));
  }, [effectiveDay]);

  // ---- Load journeys ----
  useEffect(() => {
    if (!selectedLine) return;
    setLoadingJourneys(true);
    setError(null);
    fetchJourneys({ day: effectiveDay, line: selectedLine, filter: apiFilter })
      .then(r => setJourneys(r.journeys))
      .catch(e => setError(e.message))
      .finally(() => setLoadingJourneys(false));
  }, [selectedLine, activeFilter, pickedDate, effectiveDay, apiFilter]);

  // ---- Live occupancy from Firestore lines/{line}/routes ----
  useEffect(() => {
    if (!selectedLine) return;

    setOccupancyMap({});

    const routesRef = collection(db, 'lines', selectedLine, 'routes');
    const unsub = onSnapshot(
      routesRef,
      snap => {
        const map: Record<string, number | null> = {};
        snap.docs.forEach(doc => {
          const data = doc.data();
          const occ = data?.live?.occupancy ?? null;
          map[doc.id] = typeof occ === 'number' ? occ : null;
        });
        setOccupancyMap(map);
      },
      err => console.error('❌ occupancy onSnapshot error', err)
    );

    return () => unsub();
  }, [selectedLine]);

  return (
    <Box sx={{
      width: 220, minWidth: 220, height: '100%',
      backgroundColor: C.bg,
      borderRight: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column',
      fontFamily: '"IBM Plex Sans", sans-serif',
      overflowY: 'hidden',
    }}>

      {/* Day heading */}
      <Box sx={{ px: '12px', pt: '14px', pb: '10px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', letterSpacing: '0.15em', color: C.label, textTransform: 'uppercase', mb: '4px' }}>
          Datum
        </Typography>
        <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '12px', color: activeFilter === 'date' ? C.filterActive : '#ffffff', fontWeight: 500, lineHeight: 1.3 }}>
          {formatDayHeading(effectiveDay)}
        </Typography>
      </Box>

      {/* Filter tabs */}
      <Box sx={{ p: '10px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', letterSpacing: '0.15em', color: C.label, textTransform: 'uppercase', mb: 1 }}>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
          {(['live', 'recent', 'all'] as ActiveFilter[]).map(f => (
            <Box key={f} onClick={() => setActiveFilter(f)} sx={{
              flex: 1, textAlign: 'center', py: '4px', px: '6px', borderRadius: '3px',
              cursor: 'pointer', fontSize: '11px', fontFamily: '"IBM Plex Sans"',
              color:           activeFilter === f ? C.filterActive : C.textMuted,
              backgroundColor: activeFilter === f ? C.filterBg     : 'transparent',
              border:          activeFilter === f ? '1px solid rgba(240,165,0,0.4)' : '1px solid transparent',
              transition: 'all 0.15s ease',
              '&:hover': { color: C.filterActive },
            }}>
              {f === 'live' ? 'Live' : f === 'recent' ? 'Recent' : 'All'}
            </Box>
          ))}
        </Box>

        {/* Date picker */}
        <Box onClick={() => setActiveFilter('date')} sx={{ cursor: 'pointer' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', letterSpacing: '0.15em', color: C.label, textTransform: 'uppercase', mb: '4px' }}>
            Datum wählen
          </Typography>
          <input
            type="date"
            value={pickedDate}
            max={today}
            onChange={e => { setPickedDate(e.target.value); setActiveFilter('date'); }}
            style={{
              width: '100%',
              backgroundColor: activeFilter === 'date' ? 'rgba(240,165,0,0.08)' : 'rgba(255,255,255,0.04)',
              border: activeFilter === 'date' ? '1px solid rgba(240,165,0,0.4)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '3px',
              color: activeFilter === 'date' ? '#f0a500' : 'rgba(255,255,255,0.5)',
              padding: '4px 6px', fontSize: '12px',
              fontFamily: '"IBM Plex Mono", monospace',
              outline: 'none', boxSizing: 'border-box', colorScheme: 'dark',
            }}
          />
        </Box>
      </Box>

      {/* Line selector */}
      <Box sx={{ p: '10px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', letterSpacing: '0.15em', color: C.label, textTransform: 'uppercase', mb: 1 }}>
          Linie
        </Typography>
        {loadingLines ? (
          <CircularProgress size={16} sx={{ color: C.filterActive }} />
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {lines.map(line => (
              <Box key={line} onClick={() => setSelectedLine(line)} sx={{
                px: '8px', py: '3px', borderRadius: '3px', cursor: 'pointer',
                fontSize: '12px', fontFamily: '"IBM Plex Mono"', fontWeight: 500,
                color:           selectedLine === line ? '#0d1b2a' : C.textMuted,
                backgroundColor: selectedLine === line ? C.filterActive : 'transparent',
                border:          selectedLine === line ? `1px solid ${C.filterActive}` : '1px solid rgba(255,255,255,0.1)',
                transition: 'all 0.15s ease',
                '&:hover': { color: selectedLine === line ? '#0d1b2a' : C.filterActive, borderColor: C.filterActive },
              }}>
                {line}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Journey list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: '8px 0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: '12px', pb: '6px' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', letterSpacing: '0.15em', color: C.label, textTransform: 'uppercase' }}>
            Züge
          </Typography>
          {!loadingJourneys && (
            <Typography sx={{ fontSize: '10px', color: C.textMuted, fontFamily: '"IBM Plex Mono"' }}>
              {journeys.length}
            </Typography>
          )}
        </Box>

        {loadingJourneys && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={20} sx={{ color: C.filterActive }} />
          </Box>
        )}

        {error && (
          <Typography sx={{ fontSize: '11px', color: '#f87171', px: 2, pt: 1 }}>{error}</Typography>
        )}

        {!loadingJourneys && !error && journeys.map(journey => {
          const isSelected = journey.journey_id === selectedJourneyId;
          const sColor     = statusColour(journey.status);
          const occupancy  = occupancyMap[journey.route_id] ?? null;

          return (
            <Box
              key={journey.journey_id}
              onClick={() => onSelectJourney(journey)}
              sx={{
                px: '12px', py: '8px', cursor: 'pointer',
                backgroundColor: isSelected ? C.bgSelected : 'transparent',
                borderLeft: isSelected ? `2px solid ${C.filterActive}` : '2px solid transparent',
                transition: 'background-color 0.1s ease',
                '&:hover': { backgroundColor: isSelected ? C.bgSelected : C.bgHover },
              }}
            >
              {/* Route ID + departure time */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '2px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: isSelected ? '#ffffff' : C.text, fontFamily: '"IBM Plex Mono"' }}>
                  {journey.route_id}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: C.textMuted, fontFamily: '"IBM Plex Mono"' }}>
                  {formatTime(journey.scheduled_departure)}
                </Typography>
              </Box>

              {/* Status dot */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: sColor, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '10px', color: sColor, fontFamily: '"IBM Plex Mono"', letterSpacing: '0.05em' }}>
                  {journey.status}
                </Typography>
                {occupancy !== null && (
                  <Typography sx={{ fontSize: '10px', color: C.textDim, fontFamily: '"IBM Plex Mono"', ml: 'auto' }}>
                    {occupancy}%
                  </Typography>
                )}
              </Box>

              {/* Occupancy bar */}
              <OccupancyBar occupancy={occupancy} />
            </Box>
          );
        })}

        {!loadingJourneys && !error && journeys.length === 0 && (
          <Typography sx={{ fontSize: '12px', color: C.textMuted, px: 2, pt: 2, fontFamily: '"IBM Plex Sans"' }}>
            Keine Züge gefunden.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MessagesSidebar;